import type { CookieConsentConfig } from 'vanilla-cookieconsent';
import { initSentryLazy } from '../../sentry';

declare global {
  interface Window {
    dataLayer: any;
  }
}

const getConfig = () => {
  // See https://cookieconsent.orestbida.com/reference/configuration-reference.html for configuration options.
  const config: CookieConsentConfig = {
    // Default configuration for the modal.
    root: 'body',
    autoShow: true,
    disablePageInteraction: false,
    hideFromBots: import.meta.env.PROD ? true : false, // Set this to false for dev/headless tests otherwise the modal will not be visible.
    mode: 'opt-in',
    revision: 1,

    // Default configuration for the cookie.
    cookie: {
      name: 'cc_cookie',
      domain: location.hostname,
      path: '/',
      sameSite: 'Lax',
      expiresAfterDays: 365,
    },

    guiOptions: {
      consentModal: {
        layout: 'box',
        position: 'bottom right',
        equalWeightButtons: true,
        flipButtons: false,
      },
    },

    categories: {
      necessary: {
        enabled: true, // this category is enabled by default
        readOnly: true, // this category cannot be disabled
      },
      functional: {
        services: {
          sentry: {
            label:
              'Error monitoring — helps us catch and fix bugs so your reading experience stays smooth.',
            onAccept: () => {
              initSentryLazy();
            },
            onReject: () => {},
          },
        },
      },
      analytics: {
        autoClear: {
          cookies: [
            {
              name: /^_ga/, // regex: match all cookies starting with '_ga'
            },
            {
              name: '_gid', // string: exact cookie name
            },
          ],
        },

        // https://cookieconsent.orestbida.com/reference/configuration-reference.html#category-services
        services: {
          ga: {
            label: 'Google Analytics',
            onAccept: () => {
              try {
                const GA_ANALYTICS_ID = import.meta.env
                  .REACT_APP_GOOGLE_ANALYTICS_ID;
                if (!GA_ANALYTICS_ID.length) {
                  throw new Error('Google Analytics ID is missing');
                }
                window.dataLayer = window.dataLayer || [];
                function gtag(..._args: unknown[]) {
                  (window.dataLayer as Array<any>).push(arguments);
                }
                gtag('js', new Date());
                gtag('config', GA_ANALYTICS_ID);

                // Adding the script tag dynamically to the DOM.
                const script = document.createElement('script');
                script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ANALYTICS_ID}`;
                script.async = true;
                document.body.appendChild(script);
              } catch (error) {
                console.error(error);
              }
            },
            onReject: () => {},
          },
        },
      },
    },

    language: {
      default: 'en',
      translations: {
        en: {
          consentModal: {
            title: 'We use cookies',
            description:
              'We use cookies for site functionality and analytics to enhance your experience. By accepting, you agree to our use of these cookies. You can manage your preferences or learn more about our cookie policy.',
            acceptAllBtn: 'Accept all',
            acceptNecessaryBtn: 'Reject all',
            showPreferencesBtn: 'Manage preferences',
            footer: `
            <a href="/privacy" target="_blank">Privacy Policy</a>
            <a href="/terms" target="_blank">Terms and Conditions</a>
            <a href="/impressum" target="_blank">Impressum</a>
                    `,
          },
          preferencesModal: {
            title: 'Cookie preferences',
            acceptAllBtn: 'Accept all',
            acceptNecessaryBtn: 'Reject all',
            savePreferencesBtn: 'Save preferences',
            closeIconLabel: 'Close',
            sections: [
              {
                title: 'Cookie usage',
                description:
                  'We use cookies to ensure basic site functionality and to understand how you interact with our content. You can choose which categories to enable below.',
              },
              {
                title: 'Strictly necessary cookies',
                description:
                  'These cookies are essential for the website to function properly. They enable core features like session management and cannot be disabled.',
                linkedCategory: 'necessary',
              },
              {
                title: 'Functional cookies',
                description:
                  'These cookies support features that improve your experience. This includes error monitoring so we can catch and fix bugs quickly.',
                linkedCategory: 'functional',
              },
              {
                title: 'Analytics cookies',
                description:
                  'These cookies help us understand how visitors use the site so we can improve it. All data is aggregated and anonymous.',
                linkedCategory: 'analytics',
              },
            ],
          },
        },
      },
    },
  };

  return config;
};

export default getConfig;
